import { useContext, useState } from 'react';
import { Button, NativeSelect, Stack } from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useEquifaxConsumerCreditReportForm,
  useRunEquifaxConsumerCreditReportRequest,
} from '~/core/shared/api/plugin';
import { Dialog } from '~/core/shared/components';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import {
  ApplicationContext,
  DocumentDetailsViewActionContext,
} from '../../Layout.component';

/**
 * @TODO Prevent this modal from being eager loaded on every application load.
 */
export const IndividualCreditReportModal = () => {
  const { applicationUuid, setLastApplicationUpdate, actionContexts } =
    useContext(ApplicationContext);

  const {
    action: { key },
  } = useContext(DocumentDetailsViewActionContext);

  const equifaxModalApi: UseOpenCloseReturn = actionContexts[key].modal;
  const buttonText: string = actionContexts[key].buttonText
    ? actionContexts[key].buttonText
    : 'Check ID';
  const modalHeaderText: string = actionContexts[key].modalHeaderText
    ? actionContexts[key].modalHeaderText
    : 'Equifax - Individual Credit Report';

  const [selectedUuid, setSelectedUuid] = useState<string>();

  const { formData } = useEquifaxConsumerCreditReportForm(applicationUuid);
  const { runEquifaxConsumerCreditReportRequest, isPending } =
    useRunEquifaxConsumerCreditReportRequest();

  const startRequest = useEventHandler(
    async (individualUuid: string): Promise<void> => {
      try {
        await runEquifaxConsumerCreditReportRequest({
          params: {
            uuid: applicationUuid,
            individualUuid,
          },
        });
        setLastApplicationUpdate(new Date());
        equifaxModalApi.onClose();
      } catch {
        // Error is handled in useRunEquifaxConsumerCreditReportRequest
      }
    },
  );

  return (
    <Dialog
      header={modalHeaderText}
      onOpenChange={equifaxModalApi.onOpenChange}
      open={equifaxModalApi.open}
      size="lg"
    >
      <Stack gap={4}>
        {formData && (
          <NativeSelect.Root>
            <NativeSelect.Field
              onChange={({ currentTarget }) =>
                setSelectedUuid(currentTarget.value)
              }
              placeholder="Select individual"
            >
              {Object.entries(formData.individuals).map(
                ([individualKey, data]) => (
                  <optgroup key={individualKey} label={data.optGroup}>
                    {data.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ),
              )}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        )}

        <Stack direction="row" justifyContent="end">
          <Button
            colorPalette="gray"
            fontSize="md"
            fontWeight="normal"
            onClick={equifaxModalApi.onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            colorPalette="primary"
            disabled={!selectedUuid}
            fontSize="md"
            fontWeight="bold"
            loading={isPending}
            onClick={() => selectedUuid && startRequest(selectedUuid)}
            variant="solid"
          >
            {buttonText}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
